import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, animate, useReducedMotion } from "framer-motion";
import { useReveal, EASE } from "@/lib/motion";
import { POSITIONING, SERVICES } from "@/lib/siteData";

// First white section of the home: left-aligned headline, two editorial paragraphs,
// the four service links with vertical bar separators, the WhatsApp CTA button,
// and a draggable, smoothly advancing photo carousel on the right.
const BEHOLD_FEED_URL = "https://feeds.behold.so/e2qGRhLBdKCL40uPmXeP";

export const Positioning = () => {
    const reveal = useReveal();
    const reduceMotion = useReducedMotion();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [step, setStep] = useState(340);
    const [slides, setSlides] = useState(POSITIONING.slides || []);

    const containerRef = useRef(null);
    const pauseTimeoutRef = useRef(null);
    const dragDistanceRef = useRef(0);
    const x = useMotionValue(0);

    // Fetch live posts from Instagram via Behold
    useEffect(() => {
        let isMounted = true;
        fetch(BEHOLD_FEED_URL)
            .then((res) => {
                if (!res.ok) throw new Error("Feed fetch error");
                return res.json();
            })
            .then((data) => {
                if (!isMounted) return;
                const posts = data?.posts || (Array.isArray(data) ? data : []);
                if (posts && posts.length > 0) {
                    const mapped = posts.slice(0, 6).map((post, idx) => ({
                        id: post.id || `insta-${idx}`,
                        image:
                            post.sizes?.large?.mediaUrl ||
                            post.sizes?.medium?.mediaUrl ||
                            post.mediaUrl ||
                            post.thumbnailUrl,
                        permalink: post.permalink || "https://instagram.com/rockfoxstudio",
                        caption: post.prunedCaption || post.caption || "RockFox Studio",
                    }));
                    setSlides(mapped);
                }
            })
            .catch(() => {
                // Fallback gracefully stays with POSITIONING.slides
            });

        return () => {
            isMounted = false;
        };
    }, []);

    // Measure slide step width on mount & window resize
    useEffect(() => {
        const updateStep = () => {
            if (containerRef.current) {
                const firstSlide = containerRef.current.querySelector("[data-slide]");
                if (firstSlide) {
                    const width = firstSlide.offsetWidth;
                    const isSm = window.innerWidth >= 640;
                    const gap = isSm ? 24 : 16;
                    setStep(width + gap);
                }
            }
        };

        updateStep();
        window.addEventListener("resize", updateStep);
        return () => window.removeEventListener("resize", updateStep);
    }, [slides]);

    // Animate track to current index when it changes
    useEffect(() => {
        const controls = animate(x, -currentIndex * step, {
            ease: EASE,
            duration: reduceMotion ? 0.01 : 0.7,
        });
        return controls.stop;
    }, [currentIndex, step, x, reduceMotion]);

    // Smoothly cycle through slides every 3.5s when not paused
    useEffect(() => {
        if (reduceMotion || isPaused || isDragging) return undefined;
        const interval = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % slides.length);
        }, 3500);
        return () => clearInterval(interval);
    }, [reduceMotion, isPaused, isDragging, slides.length]);

    const handleDragStart = () => {
        setIsDragging(true);
        setIsPaused(true);
        if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
    };

    const handleDragEnd = (e, { offset, velocity }) => {
        setTimeout(() => setIsDragging(false), 50);

        const isQuickSwipe = Math.abs(velocity.x) > 250;
        const isLongDrag = Math.abs(offset.x) > 40;

        if (offset.x < 0 && (isQuickSwipe || isLongDrag)) {
            setCurrentIndex((prev) => (prev + 1) % slides.length);
        } else if (offset.x > 0 && (isQuickSwipe || isLongDrag)) {
            setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length);
        } else {
            animate(x, -currentIndex * step, { ease: EASE, duration: 0.4 });
        }

        // Resume auto-play after a grace period
        if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
        pauseTimeoutRef.current = setTimeout(() => {
            setIsPaused(false);
        }, 4000);
    };

    return (
        <section className="overflow-hidden bg-paper px-6 py-[80px] md:px-12 md:py-[100px]">
            <div className="mx-auto max-w-[1440px]">
                <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-12 xl:gap-16">
                    {/* Left Column: Text Content */}
                    <div className="text-left lg:col-span-6 xl:col-span-7">
                        <motion.h2
                            className="type-display text-[clamp(32px,4vw,54px)] leading-[1.08] text-brand"
                            {...reveal(0)}
                        >
                            Criatividade que faz sua
                            <br />
                            marca ser lembrada.
                        </motion.h2>

                        <motion.div
                            className="mt-8 max-w-[620px] space-y-6 text-[clamp(17px,1.5vw,22px)] leading-relaxed text-ink"
                            {...reveal(0.08)}
                        >
                            {POSITIONING.body.map((paragraph, index) => (
                                <p key={index}>
                                    {paragraph.map((run) =>
                                        run.bold ? (
                                            <strong key={run.text} className="font-bold text-ink">
                                                {run.text}
                                            </strong>
                                        ) : (
                                            <span key={run.text}>{run.text}</span>
                                        ),
                                    )}
                                </p>
                            ))}
                        </motion.div>

                        {/* Services row with vertical separators */}
                        <motion.ul
                            className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-2 text-[clamp(15px,1.3vw,19px)] font-bold"
                            {...reveal(0.16)}
                        >
                            {SERVICES.map((service, index) => (
                                <li key={service.slug} className="flex items-center gap-x-3">
                                    {index > 0 && (
                                        <span aria-hidden="true" className="font-normal text-ink">
                                            |
                                        </span>
                                    )}
                                    <Link
                                        to={`/servicos/${service.slug}`}
                                        className="type-display text-brand transition-opacity duration-300 hover:opacity-75"
                                    >
                                        {service.shortLabel}
                                    </Link>
                                </li>
                            ))}
                        </motion.ul>

                        {/* Left-aligned CTA button */}
                        <motion.div className="mt-8" {...reveal(0.24)}>
                            <Link
                                to="/contato"
                                className="type-display inline-flex h-[50px] items-center justify-center bg-brand px-7 text-action text-paper transition-all duration-300 btn-sweep [--sweep:theme(colors.brand.deep)]"
                            >
                                {POSITIONING.action}
                            </Link>
                        </motion.div>
                    </div>

                    {/* Right Column: Photo Carousel with interactive dragging and prominence depth effect */}
                    <div
                        className="relative w-full lg:col-span-6 xl:col-span-5"
                        onMouseEnter={() => setIsPaused(true)}
                        onMouseLeave={() => !isDragging && setIsPaused(false)}
                        onTouchStart={() => setIsPaused(true)}
                        onTouchEnd={() => !isDragging && setIsPaused(false)}
                        aria-label="Carrossel de fotos"
                    >
                        <div ref={containerRef} className="relative w-full overflow-hidden py-8">
                            <motion.div
                                style={{ x }}
                                drag="x"
                                dragConstraints={{
                                    left: -((slides.length - 1) * step),
                                    right: 0,
                                }}
                                dragElastic={0.2}
                                onDragStart={handleDragStart}
                                onDragEnd={handleDragEnd}
                                className="flex items-center gap-4 sm:gap-6 cursor-grab active:cursor-grabbing touch-pan-y"
                            >
                                {slides.map((slide, idx) => {
                                    const isActive = idx === currentIndex;
                                    return (
                                        <div
                                            key={slide.id || idx}
                                            data-slide
                                            onClick={() => {
                                                if (isDragging) return;
                                                if (!isActive) {
                                                    setCurrentIndex(idx);
                                                } else if (slide.permalink) {
                                                    window.open(slide.permalink, "_blank", "noopener,noreferrer");
                                                }
                                            }}
                                            className="group relative aspect-square w-[78%] flex-shrink-0 flex items-center justify-center sm:w-[80%] cursor-pointer"
                                        >
                                            <motion.div
                                                animate={{
                                                    scale: isActive ? 1 : 0.88,
                                                    opacity: isActive ? 1 : 0.7,
                                                }}
                                                transition={{
                                                    duration: 0.65,
                                                    ease: EASE,
                                                }}
                                                className={`relative h-full w-full origin-center overflow-hidden rounded-[18px] md:rounded-[24px] bg-[#EAEAEA] transition-shadow duration-500 border border-black/[0.04] ${
                                                    isActive
                                                        ? "shadow-[0_16px_40px_-12px_rgba(0,0,0,0.18)]"
                                                        : "shadow-[0_8px_24px_-8px_rgba(0,0,0,0.08)]"
                                                }`}
                                            >
                                                {slide.image && (
                                                    <img
                                                        src={slide.image}
                                                        alt={slide.caption || `Post RockFox ${idx + 1}`}
                                                        className="h-full w-full object-cover select-none pointer-events-none transition-transform duration-500 group-hover:scale-105"
                                                        loading="lazy"
                                                        draggable="false"
                                                    />
                                                )}

                                                {/* Instagram link badge on hover */}
                                                {slide.permalink && isActive && (
                                                    <div className="absolute bottom-3 right-3 flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1.5 text-[12px] font-medium text-white backdrop-blur-md transition-opacity duration-300 opacity-90 sm:opacity-0 sm:group-hover:opacity-100">
                                                        <svg className="h-3.5 w-3.5 fill-current" viewBox="0 0 16 16">
                                                            <path d="M8 0C5.829 0 5.556.01 4.703.048 3.85.088 3.269.222 2.76.42a3.9 3.9 0 0 0-1.417.923A3.9 3.9 0 0 0 .42 2.76C.222 3.268.087 3.85.048 4.7.01 5.555 0 5.827 0 8.001c0 2.172.01 2.444.048 3.297.04.852.174 1.433.372 1.942.205.526.478.972.923 1.417.444.445.89.719 1.416.923.51.198 1.09.333 1.942.372C5.555 15.99 5.827 16 8 16s2.444-.01 3.298-.048c.851-.04 1.434-.174 1.943-.372a3.9 3.9 0 0 0 1.416-.923c.445-.445.718-.891.923-1.417.197-.509.332-1.09.372-1.942C15.99 10.445 16 10.173 16 8s-.01-2.445-.048-3.299c-.04-.851-.175-1.433-.372-1.941a3.9 3.9 0 0 0-.923-1.417A3.9 3.9 0 0 0 13.24.42c-.51-.198-1.092-.333-1.943-.372C10.443.01 10.172 0 7.998 0zm-.717 1.442h.718c2.136 0 2.389.007 3.232.046.78.035 1.204.166 1.486.275.373.145.64.319.92.599s.453.546.598.92c.11.281.24.705.275 1.485.039.843.047 1.096.047 3.231s-.008 2.389-.047 3.232c-.035.78-.166 1.203-.275 1.485a2.5 2.5 0 0 1-.599.919c-.28.28-.546.453-.92.598-.28.11-.704.24-1.485.276-.843.038-1.096.047-3.232.047s-2.39-.009-3.233-.047c-.78-.036-1.203-.166-1.485-.276a2.5 2.5 0 0 1-.92-.598 2.5 2.5 0 0 1-.6-.92c-.109-.281-.24-.705-.275-1.485-.038-.843-.046-1.096-.046-3.233s.008-2.388.046-3.231c.036-.78.166-1.204.276-1.486.145-.373.319-.64.599-.92s.546-.453.92-.598c.282-.11.705-.24 1.485-.276.738-.034 1.024-.044 2.515-.045zm4.988 1.328a.96.96 0 1 0 0 1.92.96.96 0 0 0 0-1.92m-4.27 1.122a4.109 4.109 0 1 0 0 8.217 4.109 4.109 0 0 0 0-8.217m0 1.441a2.667 2.667 0 1 1 0 5.334 2.667 2.667 0 0 1 0-5.334" />
                                                        </svg>
                                                        <span>Ver no Instagram</span>
                                                    </div>
                                                )}
                                            </motion.div>
                                        </div>
                                    );
                                })}
                            </motion.div>
                        </div>

                        {/* Navigation Dots Indicator */}
                        <div className="mt-4 flex items-center justify-center gap-2 sm:mt-6">
                            {slides.map((_, idx) => {
                                const isActive = idx === currentIndex;
                                return (
                                    <button
                                        key={idx}
                                        type="button"
                                        onClick={() => setCurrentIndex(idx)}
                                        aria-label={`Ir para o slide ${idx + 1}`}
                                        className={`h-2 rounded-full transition-all duration-300 ${
                                            isActive
                                                ? "w-7 bg-brand"
                                                : "w-2 bg-black/20 hover:bg-black/40"
                                        }`}
                                    />
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

